import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { lphGruppeFuer } from '@asas/domain';
import type { Gewerk, LphGroup, WorkPackageLibraryEntry } from '@asas/domain';

import type { FehlerBefund } from './daten/fehler';
import { klassifiziereFehler } from './daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from './daten/serialisierung';
import { trpc } from './trpc';

/**
 * Arbeitspaket-Bibliothek (AC-301): Stammarbeitspakete ansehen und — mit
 * `work_package_library:manage` — anlegen, ändern, archivieren und
 * wiederherstellen.
 *
 * Aufgeteilt wie die Nutzerverwaltung in eine REINE Ansicht
 * (`ArbeitspaketBibliothekPanel`, lädt nichts, ruft nichts auf) und einen
 * Container (`ArbeitspaketBibliothek`), der lädt, je Eintrag serialisiert und
 * nach Erfolg die SERVERANTWORT übernimmt (AC-016, AK-2). Abweichend von
 * `AdminPanel` steht der Container hier statt in `App.tsx`, damit `App.tsx`
 * nicht mit jeder Ansicht wächst.
 *
 * Geladen wird erst auf einen bewussten Klick (apps/web/CLAUDE.md, Ansichten).
 * Ohne Schreibrecht zeigt die Ansicht dieselbe Liste ohne Knöpfe — keine
 * Schreibprozedur wird je aufgerufen, also kein 403 und keine Audit-Zeile.
 */

export type Bibliotheksaktion = 'anlegen' | 'aendern' | 'archivieren' | 'wiederherstellen';

/**
 * Die Meldung zu einer gescheiterten Aktion — EINMAL je Aktion, wie
 * `meldungFuerKontoaktion`.
 *
 * Die API antwortet mit CONFLICT aus mehreren Gründen (Dublette, Code
 * vergeben, Version überholt, falscher Zustand); `klassifiziereFehler`
 * unterscheidet sie nicht, und an der Aufrufstelle wird kein Fehlercode
 * gelesen. Die Meldung nennt deshalb die Gründe, die bei DIESER Aktion
 * möglich sind. Ein `eingabe`-Befund ohne Feldbefunde kann bei dieser API nur
 * die Gruppenprüfung sein (H2): Zod liefert immer Feldbefunde.
 */
function grundmeldung(aktion: Bibliotheksaktion, befund: FehlerBefund): string {
  if (befund.art === 'konflikt') {
    switch (aktion) {
      case 'anlegen':
        return 'Im gewählten Gewerk gibt es bereits ein aktives Arbeitspaket mit dieser Bezeichnung, oder der Code ist schon vergeben.';
      case 'aendern':
        return 'Im gewählten Gewerk gibt es bereits ein aktives Arbeitspaket mit dieser Bezeichnung, der Code ist schon vergeben, oder der Eintrag wurde inzwischen geändert. Bitte neu laden und prüfen.';
      case 'archivieren':
        return 'Der Eintrag wurde inzwischen geändert oder ist bereits archiviert. Bitte neu laden.';
      case 'wiederherstellen':
        return 'Im Gewerk gibt es inzwischen ein aktives Arbeitspaket mit dieser Bezeichnung, oder der Eintrag wurde geändert. Bitte neu laden und prüfen.';
    }
  }

  // Annahme, ausdrücklich: Ein Eingabefehler OHNE Feldbefunde ist bei dieser API
  // die Gruppenprüfung (Zod liefert immer Feldbefunde). `klassifiziereFehler`
  // ordnet auch eine zu große Anfrage `eingabe` ohne Felder zu; bei den
  // Feldlängen dieser Ansicht (höchstens rund 2,5 KB) ist das nicht erreichbar.
  if (befund.art === 'eingabe' && befund.felder.length === 0) {
    return 'Die Leistungsphasen entsprechen keiner Gruppe der Mandantenkonfiguration.';
  }

  if (befund.art === 'eingabe') {
    return `${befund.meldung} (${befund.felder.map((feld) => feld.path).join(', ')})`;
  }

  if (befund.art === 'nicht_gefunden') {
    return 'Gewerk oder Arbeitspaket nicht gefunden. Bitte neu laden.';
  }

  return befund.meldung;
}

/**
 * Hängt bei ungewissem Ausgang (Netz, Server, Abbruch nach dem Absenden) die
 * Warnung an, dass die Aktion bereits gespeichert sein könnte
 * (apps/web/CLAUDE.md: Wiederholung eines Schreibzugriffs nur MIT Warnung).
 */
export function meldungFuerBibliotheksaktion(
  aktion: Bibliotheksaktion,
  befund: FehlerBefund,
): string {
  const meldung = grundmeldung(aktion, befund);

  return befund.moeglicherweiseAusgefuehrt
    ? `${meldung} Die Aktion wurde möglicherweise bereits gespeichert – bitte neu laden und prüfen, bevor Sie es erneut versuchen.`
    : meldung;
}

export interface BibliotheksFilter {
  readonly gewerkId: string;
  /** Bezeichnung der LPH-Gruppe aus der Mandantenkonfiguration, `''` = alle. */
  readonly lphGruppe: string;
  readonly archivierteZeigen: boolean;
}

export const KEIN_FILTER: BibliotheksFilter = {
  gewerkId: '',
  lphGruppe: '',
  archivierteZeigen: false,
};

/** Filtert die geladene Liste; die Reihenfolge des Servers bleibt erhalten. */
export function filtereEintraege(
  eintraege: readonly WorkPackageLibraryEntry[],
  filter: BibliotheksFilter,
  gruppen: readonly LphGroup[],
): WorkPackageLibraryEntry[] {
  return eintraege.filter(
    (eintrag) =>
      (filter.archivierteZeigen || eintrag.archivedAt === null) &&
      (filter.gewerkId === '' || eintrag.gewerkId === filter.gewerkId) &&
      (filter.lphGruppe === '' ||
        lphGruppeFuer(eintrag.leistungsphasen, gruppen)?.label === filter.lphGruppe),
  );
}

/**
 * Die Eingabe für Anlegen und Ändern, mit ALLEN Feldern ausdrücklich gesetzt
 * (`null` heißt leer) — passt damit zu `create` und, um `id` und `version`
 * ergänzt, zu `update`.
 */
export interface BibliotheksEingabe {
  readonly name: string;
  readonly gewerkId: string;
  readonly leistungsphasen: number[];
  readonly richtwertH: string | null;
  readonly code: string | null;
  readonly beschreibung: string | null;
}

/** Die Werte des Formulars, alle als Text — Stunden bleiben eine Zeichenkette (Regel 10). */
export interface FormularWerte {
  readonly name: string;
  readonly gewerkId: string;
  readonly lphGruppe: string;
  readonly richtwertH: string;
  readonly code: string;
  readonly beschreibung: string;
}

const LEERES_FORMULAR: FormularWerte = {
  name: '',
  gewerkId: '',
  lphGruppe: '',
  richtwertH: '',
  code: '',
  beschreibung: '',
};

function formularAus(
  eintrag: WorkPackageLibraryEntry,
  gruppen: readonly LphGroup[],
): FormularWerte {
  return {
    name: eintrag.name,
    gewerkId: eintrag.gewerkId,
    lphGruppe: lphGruppeFuer(eintrag.leistungsphasen, gruppen)?.label ?? '',
    richtwertH: eintrag.richtwertH ?? '',
    code: eintrag.code ?? '',
    beschreibung: eintrag.beschreibung ?? '',
  };
}

/**
 * Formular → Eingabe der API. Leere optionale Felder werden `null`; die
 * Leistungsphasen kommen aus der gewählten Gruppe. Geprüft wird am Server —
 * ein leerer Pflichtwert geht absichtlich hinaus und kommt als Feldbefund
 * zurück, statt hier eine zweite Prüfung zu pflegen.
 */
export function eingabeAus(werte: FormularWerte, gruppen: readonly LphGroup[]): BibliotheksEingabe {
  const leer = (wert: string): string | null => (wert.trim() === '' ? null : wert.trim());

  return {
    name: werte.name,
    gewerkId: werte.gewerkId,
    leistungsphasen: [
      ...(gruppen.find((gruppe) => gruppe.label === werte.lphGruppe)?.phases ?? []),
    ],
    richtwertH: leer(werte.richtwertH),
    code: leer(werte.code),
    beschreibung: leer(werte.beschreibung),
  };
}

// --- Reine Ansicht -------------------------------------------------------------------

export interface ArbeitspaketBibliothekPanelProps {
  readonly geoeffnet: boolean;
  readonly onOeffnen: () => void;
  readonly eintraege: readonly WorkPackageLibraryEntry[];
  readonly gewerke: readonly Gewerk[];
  readonly lphGruppen: readonly LphGroup[];
  readonly loading: boolean;
  /** Fertig formulierte Meldung des Ladens, nicht der rohe Fehler. */
  readonly error: string | null;
  /** Nur mit `work_package_library:manage` — sonst Lesesicht ohne Knöpfe. */
  readonly darfSchreiben: boolean;
  readonly busyIds: ReadonlySet<string>;
  readonly rowErrors: ReadonlyMap<string, string>;
  /** Meldung des letzten gescheiterten Speicherns im Formular. */
  readonly formFehler: string | null;
  readonly speichertFormular: boolean;
  /** Startwert des Filters — für Tests; im Betrieb leer. */
  readonly anfangsFilter?: BibliotheksFilter;
  readonly onNeuLaden: () => void;
  /** Liefert `true`, wenn gespeichert wurde — dann schließt das Formular. */
  readonly onAnlegen: (eingabe: BibliotheksEingabe) => Promise<boolean>;
  readonly onAendern: (
    eintrag: WorkPackageLibraryEntry,
    eingabe: BibliotheksEingabe,
  ) => Promise<boolean>;
  readonly onArchivieren: (eintrag: WorkPackageLibraryEntry) => void;
  readonly onWiederherstellen: (eintrag: WorkPackageLibraryEntry) => void;
}

interface FormularProps {
  readonly anfang: FormularWerte;
  readonly titel: string;
  readonly gewerke: readonly Gewerk[];
  readonly lphGruppen: readonly LphGroup[];
  readonly fehler: string | null;
  readonly speichert: boolean;
  readonly onSpeichern: (werte: FormularWerte) => void;
  readonly onAbbrechen: () => void;
}

/** Gewerke nach Bereich gruppiert, wie die PSP-Struktur sie ordnet. */
function GewerkOptionen({ gewerke }: { readonly gewerke: readonly Gewerk[] }): JSX.Element {
  const bereiche = [...new Set(gewerke.map((gewerk) => gewerk.bereich))];

  return (
    <>
      {bereiche.map((bereich) => (
        <optgroup key={bereich} label={bereich}>
          {gewerke
            .filter((gewerk) => gewerk.bereich === bereich)
            .map((gewerk) => (
              <option key={gewerk.id} value={gewerk.id}>
                {gewerk.code} – {gewerk.bezeichnung}
              </option>
            ))}
        </optgroup>
      ))}
    </>
  );
}

function Formular({
  anfang,
  titel,
  gewerke,
  lphGruppen,
  fehler,
  speichert,
  onSpeichern,
  onAbbrechen,
}: FormularProps): JSX.Element {
  const [werte, setWerte] = useState<FormularWerte>(anfang);
  const setze = (feld: keyof FormularWerte) => (wert: string) => {
    setWerte((vorher) => ({ ...vorher, [feld]: wert }));
  };

  return (
    <form
      aria-label={titel}
      onSubmit={(ereignis) => {
        ereignis.preventDefault();
        onSpeichern(werte);
      }}
    >
      <h3>{titel}</h3>
      <label>
        Bezeichnung
        <input
          value={werte.name}
          required
          onChange={(e) => {
            setze('name')(e.target.value);
          }}
        />
      </label>
      <label>
        Gewerk
        <select
          value={werte.gewerkId}
          required
          onChange={(e) => {
            setze('gewerkId')(e.target.value);
          }}
        >
          <option value="">Gewerk wählen …</option>
          <GewerkOptionen gewerke={gewerke} />
        </select>
      </label>
      <label>
        Leistungsphasengruppe
        <select
          value={werte.lphGruppe}
          required
          onChange={(e) => {
            setze('lphGruppe')(e.target.value);
          }}
        >
          <option value="">Gruppe wählen …</option>
          {lphGruppen.map((gruppe) => (
            <option key={gruppe.label} value={gruppe.label}>
              {gruppe.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Richtwert in Stunden (Vorbelegung, optional)
        <input
          value={werte.richtwertH}
          inputMode="decimal"
          placeholder="z. B. 40.5"
          onChange={(e) => {
            setze('richtwertH')(e.target.value);
          }}
        />
      </label>
      <label>
        Code (optional)
        <input
          value={werte.code}
          onChange={(e) => {
            setze('code')(e.target.value);
          }}
        />
      </label>
      <label>
        Beschreibung (optional)
        <textarea
          value={werte.beschreibung}
          onChange={(e) => {
            setze('beschreibung')(e.target.value);
          }}
        />
      </label>
      <button type="submit" disabled={speichert}>
        Speichern
      </button>
      <button type="button" disabled={speichert} onClick={onAbbrechen}>
        Abbrechen
      </button>
      {fehler === null ? null : <p role="alert">{fehler}</p>}
    </form>
  );
}

type FormularModus =
  | { readonly art: 'zu' }
  | { readonly art: 'neu' }
  | { readonly art: 'bearbeiten'; readonly eintrag: WorkPackageLibraryEntry };

export function ArbeitspaketBibliothekPanel({
  geoeffnet,
  onOeffnen,
  eintraege,
  gewerke,
  lphGruppen,
  loading,
  error,
  darfSchreiben,
  busyIds,
  rowErrors,
  formFehler,
  speichertFormular,
  anfangsFilter = KEIN_FILTER,
  onNeuLaden,
  onAnlegen,
  onAendern,
  onArchivieren,
  onWiederherstellen,
}: ArbeitspaketBibliothekPanelProps): JSX.Element {
  const [filter, setFilter] = useState<BibliotheksFilter>(anfangsFilter);
  const [modus, setModus] = useState<FormularModus>({ art: 'zu' });

  if (!geoeffnet) {
    return (
      <section>
        <button type="button" onClick={onOeffnen}>
          Arbeitspaket-Bibliothek öffnen
        </button>
      </section>
    );
  }

  if (loading) {
    return <p>Arbeitspakete werden geladen …</p>;
  }

  if (error !== null) {
    return <p role="alert">{error}</p>;
  }

  const gewerkNach = new Map(gewerke.map((gewerk) => [gewerk.id, gewerk]));
  const sichtbar = filtereEintraege(eintraege, filter, lphGruppen);

  return (
    <section>
      <h2>Arbeitspaket-Bibliothek</h2>
      <p>Der Richtwert ist eine Vorbelegung für neue Projektinstanzen, kein verbindlicher Plan.</p>

      <fieldset>
        <legend>Filter</legend>
        <label>
          Gewerk
          <select
            value={filter.gewerkId}
            onChange={(e) => {
              setFilter({ ...filter, gewerkId: e.target.value });
            }}
          >
            <option value="">alle</option>
            <GewerkOptionen gewerke={gewerke} />
          </select>
        </label>
        <label>
          Leistungsphasengruppe
          <select
            value={filter.lphGruppe}
            onChange={(e) => {
              setFilter({ ...filter, lphGruppe: e.target.value });
            }}
          >
            <option value="">alle</option>
            {lphGruppen.map((gruppe) => (
              <option key={gruppe.label} value={gruppe.label}>
                {gruppe.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={filter.archivierteZeigen}
            onChange={(e) => {
              setFilter({ ...filter, archivierteZeigen: e.target.checked });
            }}
          />
          Archivierte zeigen
        </label>
        <button type="button" onClick={onNeuLaden}>
          Neu laden
        </button>
      </fieldset>

      {darfSchreiben && modus.art === 'zu' ? (
        <button
          type="button"
          onClick={() => {
            setModus({ art: 'neu' });
          }}
        >
          Neues Arbeitspaket
        </button>
      ) : null}

      {darfSchreiben && modus.art !== 'zu' ? (
        <Formular
          key={modus.art === 'bearbeiten' ? modus.eintrag.id : 'neu'}
          titel={modus.art === 'neu' ? 'Neues Arbeitspaket' : 'Arbeitspaket bearbeiten'}
          anfang={
            modus.art === 'bearbeiten' ? formularAus(modus.eintrag, lphGruppen) : LEERES_FORMULAR
          }
          gewerke={gewerke}
          lphGruppen={lphGruppen}
          fehler={formFehler}
          speichert={speichertFormular}
          onAbbrechen={() => {
            setModus({ art: 'zu' });
          }}
          onSpeichern={(werte) => {
            const eingabe = eingabeAus(werte, lphGruppen);
            const gespeichert =
              modus.art === 'bearbeiten' ? onAendern(modus.eintrag, eingabe) : onAnlegen(eingabe);

            void gespeichert.then((ok) => {
              if (ok) setModus({ art: 'zu' });
            });
          }}
        />
      ) : null}

      {sichtbar.length === 0 ? (
        <p>Keine Arbeitspakete für diese Auswahl.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Bezeichnung</th>
              <th>Gewerk</th>
              <th>Leistungsphasengruppe</th>
              <th>Richtwert (h)</th>
              <th>Status</th>
              {darfSchreiben ? <th>Aktion</th> : null}
              <th />
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((eintrag) => {
              const gewerk = gewerkNach.get(eintrag.gewerkId);
              const gruppe = lphGruppeFuer(eintrag.leistungsphasen, lphGruppen);
              const busy = busyIds.has(eintrag.id);
              const zeilenFehler = rowErrors.get(eintrag.id);

              return (
                <tr key={eintrag.id} data-testid={`eintrag-${eintrag.id}`}>
                  <td>{eintrag.code ?? '—'}</td>
                  <td>{eintrag.name}</td>
                  <td>{gewerk === undefined ? '—' : `${gewerk.code} – ${gewerk.bezeichnung}`}</td>
                  <td>
                    {gruppe?.label ??
                      `LPH ${eintrag.leistungsphasen.join('+')} (keine passende Gruppe)`}
                  </td>
                  <td>{eintrag.richtwertH ?? '—'}</td>
                  <td>{eintrag.archivedAt === null ? 'aktiv' : 'archiviert'}</td>
                  {darfSchreiben ? (
                    <td>
                      {eintrag.archivedAt === null ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              setModus({ art: 'bearbeiten', eintrag });
                            }}
                          >
                            Bearbeiten
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              onArchivieren(eintrag);
                            }}
                          >
                            Archivieren
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            onWiederherstellen(eintrag);
                          }}
                        >
                          Wiederherstellen
                        </button>
                      )}
                    </td>
                  ) : null}
                  <td role={zeilenFehler === undefined ? undefined : 'alert'}>{zeilenFehler}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// --- Container -------------------------------------------------------------------------

export interface ArbeitspaketBibliothekProps {
  readonly darfSchreiben: boolean;
}

/**
 * Lädt nach dem Öffnen Liste (mit Archivierten, gefiltert wird in der
 * Ansicht), Gewerke und die LPH-Gruppierung — genau die Prozeduren aus
 * `ANSICHTEN.arbeitspaketBibliothek`.
 */
export function ArbeitspaketBibliothek({
  darfSchreiben,
}: ArbeitspaketBibliothekProps): JSX.Element {
  const [geoeffnet, setGeoeffnet] = useState(false);
  const [ladeZaehler, setLadeZaehler] = useState(0);
  const [eintraege, setEintraege] = useState<readonly WorkPackageLibraryEntry[]>([]);
  const [gewerke, setGewerke] = useState<readonly Gewerk[]>([]);
  const [lphGruppen, setLphGruppen] = useState<readonly LphGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [formFehler, setFormFehler] = useState<string | null>(null);
  const [speichertFormular, setSpeichertFormular] = useState(false);

  useEffect(() => {
    if (!geoeffnet) return;

    let mounted = true;
    setLoading(true);
    setError(null);

    void Promise.all([
      trpc.workPackageLibrary.list.query({ includeArchived: true }),
      trpc.workPackageLibrary.gewerke.query(),
      trpc.tenant.settings.query(),
    ])
      .then(([liste, geladeneGewerke, einstellungen]) => {
        if (!mounted) return;
        setEintraege(liste);
        setGewerke(geladeneGewerke);
        setLphGruppen(einstellungen.lphGroups);
        setRowErrors(new Map());
      })
      .catch((fehler: unknown) => {
        if (mounted) setError(klassifiziereFehler(fehler, { schreibzugriff: false }).meldung);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [geoeffnet, ladeZaehler]);

  /** Übernimmt die Serverantwort in die Liste (ersetzt oder hängt an). */
  function uebernimm(aktualisiert: WorkPackageLibraryEntry): void {
    setEintraege((vorher) =>
      vorher.some((eintrag) => eintrag.id === aktualisiert.id)
        ? vorher.map((eintrag) => (eintrag.id === aktualisiert.id ? aktualisiert : eintrag))
        : [...vorher, aktualisiert],
    );
  }

  async function speichere(
    aktion: 'anlegen' | 'aendern',
    aufruf: () => Promise<WorkPackageLibraryEntry>,
    schluessel: string,
  ): Promise<boolean> {
    setSpeichertFormular(true);
    setFormFehler(null);

    try {
      uebernimm(await geteilterSerialisierer.reihe(schluessel, aufruf));
      return true;
    } catch (fehler) {
      setFormFehler(
        meldungFuerBibliotheksaktion(aktion, klassifiziereFehler(fehler, { schreibzugriff: true })),
      );
      return false;
    } finally {
      setSpeichertFormular(false);
    }
  }

  async function zeilenaktion(
    eintrag: WorkPackageLibraryEntry,
    aktion: 'archivieren' | 'wiederherstellen',
  ): Promise<void> {
    setBusyIds((vorher) => new Set(vorher).add(eintrag.id));

    try {
      const eingabe = { id: eintrag.id, version: eintrag.version };
      const aktualisiert = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('work_package_library', eintrag.id),
        () =>
          aktion === 'archivieren'
            ? trpc.workPackageLibrary.archive.mutate(eingabe)
            : trpc.workPackageLibrary.restore.mutate(eingabe),
      );
      uebernimm(aktualisiert);
      setRowErrors((vorher) => {
        const neu = new Map(vorher);
        neu.delete(eintrag.id);
        return neu;
      });
    } catch (fehler) {
      const befund = klassifiziereFehler(fehler, { schreibzugriff: true });
      setRowErrors((vorher) =>
        new Map(vorher).set(eintrag.id, meldungFuerBibliotheksaktion(aktion, befund)),
      );
    } finally {
      setBusyIds((vorher) => {
        const neu = new Set(vorher);
        neu.delete(eintrag.id);
        return neu;
      });
    }
  }

  return (
    <ArbeitspaketBibliothekPanel
      geoeffnet={geoeffnet}
      onOeffnen={() => {
        setGeoeffnet(true);
      }}
      eintraege={eintraege}
      gewerke={gewerke}
      lphGruppen={lphGruppen}
      loading={loading}
      error={error}
      darfSchreiben={darfSchreiben}
      busyIds={busyIds}
      rowErrors={rowErrors}
      formFehler={formFehler}
      speichertFormular={speichertFormular}
      onNeuLaden={() => {
        setLadeZaehler((n) => n + 1);
      }}
      onAnlegen={(eingabe) =>
        // Ein neuer Eintrag hat noch keine Id; Schlüssel ist die Bibliothek
        // des Mandanten, damit zwei Anlagen aus diesem Tab nacheinander laufen.
        speichere(
          'anlegen',
          () => trpc.workPackageLibrary.create.mutate(eingabe),
          'work_package_library:neu',
        )
      }
      onAendern={(eintrag, eingabe) =>
        speichere(
          'aendern',
          () =>
            trpc.workPackageLibrary.update.mutate({
              ...eingabe,
              id: eintrag.id,
              version: eintrag.version,
            }),
          entitaetsSchluessel('work_package_library', eintrag.id),
        )
      }
      onArchivieren={(eintrag) => {
        void zeilenaktion(eintrag, 'archivieren');
      }}
      onWiederherstellen={(eintrag) => {
        void zeilenaktion(eintrag, 'wiederherstellen');
      }}
    />
  );
}
